/**
 * Auth0 integration for the native Capacitor shell.
 *
 * In a browser the SPA does a full-page redirect to Auth0 Universal Login and
 * back to `window.location.origin`. Inside a native WebView that origin is
 * `capacitor://localhost` (iOS) / `http://localhost` (Android) and a full-page
 * redirect would navigate the app away with no way back. Instead we:
 *
 *  1. Send Auth0 a custom-scheme deep link as the `redirect_uri`.
 *  2. Open Universal Login in the system browser (SFSafariViewController /
 *     Chrome Custom Tabs) via `@capacitor/browser` so SSO cookies are shared.
 *  3. Catch the deep-link callback with `@capacitor/app`'s `appUrlOpen` (and
 *     `getLaunchUrl` on cold start) and hand the URL to the SDK's
 *     `handleRedirectCallback`, then close the browser.
 *
 * Every export is a no-op / falls back to web behaviour when not running
 * natively, so importing this module is safe from shared code.
 */
import { useEffect, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { App as CapApp } from "@capacitor/app";
import { Browser } from "@capacitor/browser";

import { isNativePlatform } from "@/lib/native";

const AUTH0_DOMAIN = import.meta.env.VITE_AUTH0_DOMAIN;
/** Must match `appId` in capacitor.config.ts and the native URL scheme. */
const APP_ID = "ai.twido.app";

/** Authorization codes are single-use — guard against StrictMode double-handling. */
const processedCallbackKeys = new Set<string>();

/**
 * The URI Auth0 redirects back to after login. On native this is the app's
 * custom-scheme deep link (Auth0's Capacitor convention); on web it is the
 * current origin. Must be listed in the Auth0 application's Allowed Callback
 * URLs.
 */
export function authRedirectUri(): string {
  if (isNativePlatform() && AUTH0_DOMAIN) {
    return `${APP_ID}://${AUTH0_DOMAIN}/capacitor/${APP_ID}/callback`;
  }
  return window.location.origin;
}

/**
 * Where Auth0 returns the user after logout. The deep link on native, the
 * origin on web. Must be listed in the Auth0 application's Allowed Logout URLs.
 */
export function authReturnTo(): string {
  return authRedirectUri();
}

/** True when `url` is an Auth0 OAuth callback for this native app. */
export function isNativeAuthCallbackUrl(url: string): boolean {
  const callbackPrefix = authRedirectUri();
  if (!url.startsWith(callbackPrefix)) return false;
  return (
    url.includes("state=") &&
    (url.includes("code=") || url.includes("error="))
  );
}

/** Dedup key for a callback URL (`code` or `error` param). */
export function nativeAuthCallbackKey(url: string): string | null {
  try {
    const parsed = new URL(url);
    return (
      parsed.searchParams.get("code") ??
      parsed.searchParams.get("error") ??
      null
    );
  } catch {
    return null;
  }
}

/**
 * Exchange an Auth0 deep-link callback for a session. Returns `false` when the
 * URL is not a callback or was already processed.
 */
export async function processNativeAuthCallback(
  url: string,
  handleRedirectCallback: (url: string) => Promise<unknown>,
): Promise<boolean> {
  if (!isNativeAuthCallbackUrl(url)) return false;

  const key = nativeAuthCallbackKey(url);
  if (key) {
    if (processedCallbackKeys.has(key)) return false;
    processedCallbackKeys.add(key);
  }

  await handleRedirectCallback(url);
  return true;
}

/**
 * `openUrl` handler for `loginWithRedirect` / `logout`. On native, opens the
 * Auth0 URL in the system browser; on web returns `undefined` so the SDK uses
 * its default full-page redirect.
 */
export function nativeOpenUrl(): ((url: string) => Promise<void>) | undefined {
  if (!isNativePlatform()) return undefined;
  return async (url: string) => {
    await Browser.open({ url, windowName: "_self" });
  };
}

async function handleIncomingAuthUrl(
  url: string,
  handleRedirectCallback: (url: string) => Promise<unknown>,
): Promise<void> {
  try {
    await processNativeAuthCallback(url, handleRedirectCallback);
  } catch (err) {
    console.error("Native Auth0 callback handling failed", err);
  } finally {
    // Close the in-app browser whether login completed or the user cancelled.
    await Browser.close().catch(() => {});
  }
}

/**
 * Registers the deep-link listener that completes the Auth0 login inside the
 * native app. No-op on web. Mount once, high in the tree, inside the
 * `Auth0Provider`.
 */
export function useNativeAuthCallback(): void {
  const { handleRedirectCallback } = useAuth0();
  const handleRedirectRef = useRef(handleRedirectCallback);

  useEffect(() => {
    handleRedirectRef.current = handleRedirectCallback;
  }, [handleRedirectCallback]);

  useEffect(() => {
    if (!isNativePlatform()) return;

    let remove: (() => void) | undefined;

    const onUrl = (url: string) =>
      handleIncomingAuthUrl(url, (next) => handleRedirectRef.current(next));

    // Cold start: iOS delivers the OAuth callback via getLaunchUrl, not appUrlOpen.
    void CapApp.getLaunchUrl()
      .then((result) => {
        if (result?.url) return onUrl(result.url);
      })
      .catch(() => {});

    const registration = CapApp.addListener("appUrlOpen", ({ url }) => onUrl(url));

    void registration.then((listener) => {
      remove = () => void listener.remove();
    });

    return () => remove?.();
  }, []);
}
